import React, { useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';

import { EMPTY_ERROR_MESSAGE } from '../ContributionEmployeesSettings/utils/getErrorMessage';
import { TaxType } from '../Contributions/contributions.types';

import { ChangedContributionRecord } from './useChangedContributionRecords.store';

interface Props {
  record?: ChangedContributionRecord;
  taxYears?: string[];
  areErrorsShown?: boolean;
  hasAmountError?: boolean;
  hasDateError?: boolean;
  isLoading?: boolean;
}

export const useGetEditFormFields = ({ isLoading, record, taxYears, areErrorsShown, hasAmountError, hasDateError }: Props) => {
  const fields = useMemo(() => [
    ...(record?.employeeFullName
      ? [
          {
            name: 'employeeFullName',
            type: FieldTypes.Text,
            label: 'Employee',
            isNarrowLabelMode: true,
            defaultValue: record?.employeeFullName,
            disabled: true,
          } as Field,
        ]
      : []),
    ...(record?.planCode
      ? [
          {
            name: 'planCode',
            type: FieldTypes.Text,
            label: 'Account',
            isNarrowLabelMode: true,
            defaultValue: record?.planCode,
            disabled: true,
          } as Field,
        ]
      : []),
    {
      placeholder: 'Enter amount',
      name: 'employeeContributionAmount',
      type: FieldTypes.Currency,
      label: 'Employee amount',
      isNarrowLabelMode: true,
      defaultValue: record?.employeeContributionAmount,
      allowNegativeValue: true,
      ...areErrorsShown && hasAmountError ? {
        manualError: EMPTY_ERROR_MESSAGE,
      } : {},
      disabled: isLoading,
    },
    {
      placeholder: 'Enter amount',
      name: 'employerContributionAmount',
      type: FieldTypes.Currency,
      label: 'Employer amount',
      isNarrowLabelMode: true,
      defaultValue: record?.employerContributionAmount,
      allowNegativeValue: true,
      ...areErrorsShown && hasAmountError ? {
        manualError: EMPTY_ERROR_MESSAGE,
      } : {},
      disabled: isLoading,
    },
    {
      placeholder: 'Select date',
      name: 'contributionDateOn',
      type: FieldTypes.Date,
      label: 'Date',
      isNarrowLabelMode: true,
      isManualDateInput: true,
      ...record?.contributionDateOn ? {
        defaultValue: dayjs(record.contributionDateOn).format(DEFAULT_DATE_FORMAT)
      } : {},
      ...areErrorsShown && hasDateError ? {
        manualError: EMPTY_ERROR_MESSAGE,
      } : {},
      disabled: isLoading,
    },
    {
      placeholder: 'Select tax year',
      name: 'taxYear',
      type: FieldTypes.Dropdown,
      label: 'Tax year',
      isNarrowLabelMode: true,
      options: taxYears?.map((taxYear) => ({
        key: taxYear,
        value: taxYear,
        title: taxYear,
      })),
      singleMode: true,
      defaultValue: record?.taxYear,
      disabled: isLoading,
    },
    {
      placeholder: 'Select tax type',
      name: 'taxType',
      type: FieldTypes.Dropdown,
      label: 'Tax type',
      isNarrowLabelMode: true,
      options: [
        {
          key: TaxType.PRE_TAX,
          value: TaxType.PRE_TAX,
          title: 'Pre-tax',
        },
        {
          key: TaxType.POST_TAX,
          value: TaxType.POST_TAX,
          title: 'Post-tax',
        },
      ],
      singleMode: true,
      defaultValue: record?.taxType,
      disabled: isLoading,
    },
    {
      placeholder: 'Enter memo',
      name: 'memo',
      type: FieldTypes.Text,
      label: (
        <Box>
          <Inscription>Memo</Inscription>
          <Inscription size="small" weight="normal" lineHeight="18px" color="textSecondary">
            Optional. If not entered, the contribution date and type will be displayed on the members account.
          </Inscription>
        </Box>
      ),
      isNarrowLabelMode: true,
      defaultValue: record?.memo,
      disabled: isLoading,
    },
  ] as Field[], [record, taxYears, areErrorsShown, hasAmountError, hasDateError, isLoading]);

  return fields;
};
